import React, { FC, useState } from 'react';
import styled from 'styled-components';

export interface PhotoProperties {
  src: string;
  alt: string;
  width?: number;
  height?: number;
  isSquircle?: boolean;
}

interface AppShapedImageProps {
  photoProperties: PhotoProperties;
  width?: number;
  height?: number;
  borderRadius?: string;
  active?: boolean;
  activeBorderColor?: string;
  borderReservedSpace?: boolean;
  className?: string;
}

const Image = styled.img<{ loaded: boolean; isSquircle: boolean; borderRadius: string }>`
  max-width: 100%;
  height: auto;
  opacity: 0;
  transition: opacity 300ms ease-in-out;

  ${({ loaded }) => (loaded ? 'opacity: 1;' : 'position: absolute;')}
  
  ${({ isSquircle, borderRadius }) =>
    isSquircle
      ? `
        mask: url(/device-images/squircle.png) 50% 0% / 100% no-repeat;
        -webkit-mask: url(/device-images/squircle.png) 50% 0% / 100% no-repeat;
      `
      : `
        border-radius: ${borderRadius};
      `}
`;

const PlaceholderSvg = styled.svg<{ hide: boolean; isSquircle: boolean; borderRadius: string }>`
  width: 100%;
  height: 100%;
  fill: #e2e2e2;

  ${({ hide }) => (hide ? 'display: none;' : '')}
  
  ${({ isSquircle, borderRadius }) =>
    isSquircle
      ? `
        mask: url(/device-images/squircle.png) 50% 0% / 100% no-repeat;
        -webkit-mask: url(/device-images/squircle.png) 50% 0% / 100% no-repeat;
      `
      : `
        border-radius: ${borderRadius};
      `}
`;

const ActiveOuterWrapper = styled.div<{ isSquircle: boolean; width: number; height: number; borderRadius: string; borderColor: string; active: boolean }>`
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  max-width: ${({ width }) => width}px;
  width: 100%;
  aspect-ratio: ${({ width, height }) => `${width} / ${height}`};
  box-sizing: border-box;

  ${({ active, borderColor, isSquircle, borderRadius }) =>
    active &&
    (isSquircle
      ? `
        background-color: ${borderColor};
        mask: url(/device-images/squircle.png) 50% 0% / 100% no-repeat;
        -webkit-mask: url(/device-images/squircle.png) 50% 0% / 100% no-repeat;
      `
      : `
        background-color: ${borderColor};
        border-radius: ${borderRadius};
      `)}
`;

const ActiveGapWrapper = styled.div<{ isSquircle: boolean; width: number; height: number; borderRadius: string; active: boolean }>`
  display: flex;
  justify-content: center;
  align-items: center;
  width: calc(100% - 6px);
  height: calc(100% - 6px);
  box-sizing: border-box;
  background-color: ${({ active }) => (active ? 'var(--sys-color--surface, #ffffff)' : 'transparent')};

  ${({ active, isSquircle, borderRadius }) =>
    active &&
    (isSquircle
      ? `
        mask: url(/device-images/squircle.png) 50% 0% / 100% no-repeat;
        -webkit-mask: url(/device-images/squircle.png) 50% 0% / 100% no-repeat;
      `
      : `
        border-radius: ${borderRadius};
      `)}
`;

const StandardContainer = styled.div<{ width: number; height: number }>`
  display: flex;
  justify-content: center;
  align-items: center;
  max-width: ${({ width }) => width}px;
  width: 100%;
  aspect-ratio: ${({ width, height }) => `${width} / ${height}`};
  position: relative;
`;

const ActiveStandardContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: calc(100% - 4px);
  height: calc(100% - 4px);
  position: relative;
`;

export const AppShapedImage: FC<AppShapedImageProps> = ({
  photoProperties,
  width = 110,
  height = 110,
  borderRadius = '24px',
  active = false,
  activeBorderColor = 'var(--sys-color--primary)',
  borderReservedSpace = false,
  className,
}) => {
  const [loaded, setLoaded] = useState(false);

  const handleImageLoaded = () => {
    setLoaded(true);
  };

  const parsedRadius = parseInt(borderRadius, 10) || 0;

  const {
    src,
    alt,
    width: photoWidth,
    height: photoHeight,
    isSquircle = false,
  } = photoProperties;

  // Proportionally scale dimensions if photo asset dimensions are provided
  let finalWidth = width;
  let finalHeight = height;
  
  if (photoWidth && photoHeight) {
    if (photoWidth > photoHeight) {
      finalHeight = Math.round(height * (photoHeight / photoWidth));
    } else if (photoHeight > photoWidth) {
      finalWidth = Math.round(width * (photoWidth / photoHeight));
    }
  }

  const imageChildren = (
    <>
      <PlaceholderSvg
        hide={loaded}
        isSquircle={isSquircle}
        borderRadius={borderRadius}
        width="100%"
        height="100%"
        viewBox={finalWidth && finalHeight ? `0 0 ${finalWidth} ${finalHeight}` : undefined}
      >
        <rect width="100%" height="100%" />
      </PlaceholderSvg>
      <Image
        src={src}
        alt={alt}
        loaded={loaded}
        onLoad={handleImageLoaded}
        isSquircle={isSquircle}
        borderRadius={borderRadius}
        style={{ width: '100%', height: '100%', objectFit: 'contain' }}
      />
    </>
  );

  const shouldRenderWrappers = active || borderReservedSpace;

  if (shouldRenderWrappers) {
    const outerWidth = finalWidth + 10;
    const outerHeight = finalHeight + 10;
    const gapWidth = finalWidth + 4;
    const gapHeight = finalHeight + 4;
    const gapRadius = `${parsedRadius + 2}px`;
    const outerRadius = `${parsedRadius + 5}px`;

    return (
      <ActiveOuterWrapper
        active={active}
        isSquircle={isSquircle}
        width={outerWidth}
        height={outerHeight}
        borderRadius={outerRadius}
        borderColor={activeBorderColor}
        className={className}
      >
        <ActiveGapWrapper
          active={active}
          isSquircle={isSquircle}
          width={gapWidth}
          height={gapHeight}
          borderRadius={gapRadius}
        >
          <ActiveStandardContainer>
            {imageChildren}
          </ActiveStandardContainer>
        </ActiveGapWrapper>
      </ActiveOuterWrapper>
    );
  }

  return (
    <StandardContainer width={finalWidth} height={finalHeight} className={className}>
      {imageChildren}
    </StandardContainer>
  );
};

export default AppShapedImage;
